import usePopup from '../hooks/usePopup';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PopupContext from '../contexts/PopupContext';
import { toFilterPlate } from '../migrateScope';
import PopupReport from '../components/PopupReport';
import PopupBoard from '../components/PopupBoard';
import PopupInnerContext from '../contexts/PopupInnerContext';
import PeriodsContext from '../contexts/PeriodsContext';
import { V3SelectedAppContext } from './SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from './SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import { DashboardFiltersContext } from './DashboardFiltersProvider';

const PopupProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { isOpen, open, close: closePopup } = usePopup();
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const [selectedReport, setSelectedReport] = useState<
    ReportType | undefined
  >();
  const [selectedBoard, setSelectedBoard] = useState<
    PerformanceBoardTypes.Board | Board | undefined
  >();
  const [unSavedFilter, setUnSavedFilter] = useState<
    UnSavedFilter | undefined
  >();

  const [runTimeFilters, setRunTimeFilters] = useState<FilterPlateType[]>([]);
  const [scopeFilters, setScopeFilters] = useState<FilterPlateType[]>([]);
  const [runTimeDateRange, setRunTimeDateRange] = useState<
    DateRangeInput | undefined
  >();
  const [bonusPeriodId, setBonusPeriodId] = useState<string>('current');
  const [gadgetWithDrillDown, setGadgetWithDrillDown] = useState<
    DashboardGadget | undefined
  >();
  const [selectedScorecardPeriod, setSelectedScorecardPeriod] = useState<
    Period | undefined
  >();
  const [availableScorecardPeriods, setAvailableScorecardPeriods] = useState<
    Period[]
  >([]);
  const [poppedUpKpi, setPoppedUpKpi] = useState<
    Scorecards.ScorecardKpi | Scorecards.ManualKpiRow | undefined
  >();
  const [origin, setOrigin] = useState<
    'Dashboard' | 'Goal' | 'Scorecard' | 'DriverBonusProgramReport' | undefined
  >();

  const close = useCallback(() => {
    setOrigin(undefined);
    setUnSavedFilter(undefined);
    setPoppedUpKpi(undefined);
    closePopup();
  }, [closePopup]);

  const openPopupReport = useCallback(
    ({
      drillDowns,
      origin,
      scorecardPeriodSettings,
      dashboardGadget,
      selectedReport,
      unSavedFilter,
    }: {
      drillDowns: FixedFilter[];
      origin: 'Dashboard' | 'Goal' | 'Scorecard' | 'DriverBonusProgramReport';
      scorecardPeriodSettings?: {
        kpi: Scorecards.ScorecardKpi | Scorecards.ManualKpiRow;
        selectedPeriod: Period;
        availablePeriods: Period[];
      };
      dashboardGadget?: DashboardGadget;
      selectedReport: ReportType;
      unSavedFilter?: UnSavedFilter;
    }) => {
      setOrigin(origin);
      setSelectedBoard(undefined);
      setAvailableScorecardPeriods(
        scorecardPeriodSettings ? scorecardPeriodSettings.availablePeriods : [],
      );
      setSelectedScorecardPeriod(
        scorecardPeriodSettings
          ? scorecardPeriodSettings.selectedPeriod
          : undefined,
      );
      setPoppedUpKpi(
        scorecardPeriodSettings ? scorecardPeriodSettings.kpi : undefined,
      );
      if (dashboardGadget) {
        setGadgetWithDrillDown(
          dashboardGadget
            ? {
                ...dashboardGadget,
                drillDowns: [
                  ...dashboardGadget.drillDowns,
                  ...drillDowns.map(toFilterPlate),
                ],
              }
            : undefined,
        );
      }
      setSelectedReport(selectedReport);
      setUnSavedFilter(unSavedFilter);

      open();
    },
    [open],
  );

  const openPopupBoard = useCallback(
    ({
      popupFilters,
      scope,
      popupDateRange,
      bonusPeriodId,
      o,
      scorecardPeriodSettings,
      dashboardGadget,
      unSavedFilter,
      selectedBoard,
    }: {
      popupFilters: FilterPlateType[];
      scope: FilterPlateType[];
      popupDateRange?: DateRangeInput;
      bonusPeriodId?: string;
      o?: 'Dashboard' | 'Goal' | 'Scorecard';
      scorecardPeriodSettings?: {
        kpi: Scorecards.ScorecardKpi;
        selectedPeriod: Period;
        availablePeriods: Period[];
      };
      dashboardGadget?: DashboardGadget;
      unSavedFilter?: UnSavedFilter;
      selectedBoard: Board | PerformanceBoardTypes.Board;
    }) => {
      setOrigin(o);
      setSelectedReport(undefined);
      setRunTimeFilters(popupFilters);
      setScopeFilters(scope);
      if (bonusPeriodId) {
        setBonusPeriodId(bonusPeriodId);
        setRunTimeDateRange(undefined);
      } else if (popupDateRange) {
        setRunTimeDateRange(popupDateRange);
      } else {
        setRunTimeDateRange(undefined);
      }
      setAvailableScorecardPeriods(
        scorecardPeriodSettings ? scorecardPeriodSettings.availablePeriods : [],
      );
      setSelectedScorecardPeriod(
        scorecardPeriodSettings
          ? scorecardPeriodSettings.selectedPeriod
          : undefined,
      );
      setPoppedUpKpi(
        scorecardPeriodSettings ? scorecardPeriodSettings.kpi : undefined,
      );
      setUnSavedFilter(unSavedFilter);
      if (dashboardGadget) {
        setGadgetWithDrillDown(dashboardGadget);
      }
      setSelectedBoard(selectedBoard);

      open();
    },
    [open],
  );

  // A popup opened from a filtered dashboard already has the dashboard's
  // filters, from the gadget it was opened from (see usePopupCallback), so
  // nothing inside it applies them again
  const Popup = useMemo(() => {
    return (
      <DashboardFiltersContext.Provider value={undefined}>
        <PopupReport
          isOpen={isOpen}
          report={selectedReport}
          close={close}
          parentDashboardGadget={gadgetWithDrillDown}
          unSavedFilter={unSavedFilter}
        />
        <PopupBoard
          isOpen={isOpen}
          board={selectedBoard}
          close={close}
          runTimeFilters={runTimeFilters}
          scopeFilters={scopeFilters}
          runTimeDateRange={runTimeDateRange}
          bonusPeriodId={bonusPeriodId}
        />
      </DashboardFiltersContext.Provider>
    );
  }, [
    bonusPeriodId,
    close,
    gadgetWithDrillDown,
    isOpen,
    runTimeDateRange,
    runTimeFilters,
    scopeFilters,
    selectedBoard,
    selectedReport,
    unSavedFilter,
  ]);

  const isBonusApp =
    selectedProductApp && appTypeCheckers.isBonusApp(selectedProductApp);

  return (
    <PopupContext.Provider
      value={{
        isOpen,
        openPopupReport,
        openPopupBoard,
        close,
        selectedReport,
        selectedBoard,
        origin,
        poppedUpKpi,
        dashboardGadget: gadgetWithDrillDown,
      }}
    >
      {children}
      <PopupInnerContext.Provider
        value={{
          isPopup: true,
        }}
      >
        {isBonusApp && (
          <PeriodsContext.Provider
            value={{
              periods: availableScorecardPeriods,
              selectedPeriod: selectedScorecardPeriod,
              setSelectedPeriod: setSelectedScorecardPeriod,
            }}
          >
            {Popup}
          </PeriodsContext.Provider>
        )}
        {!isBonusApp && <>{Popup}</>}
      </PopupInnerContext.Provider>
    </PopupContext.Provider>
  );
};

export default PopupProvider;
